/**
 * @module playerManager
 * @private
 */
import { addEventListener, nativeCallback, wefmafActionResponseStates } from "../webmafActions";
import { emit } from "../../../web/manager/player/listeners";
import states from "../../../web/manager/player/states";
import { commandErrors } from "./ps4PlayerErrors";
import { youbora } from "./youbora";
import isBrowser from "../../../utils/isBrowser";

let intervalPlayTime = null;
let currentTime = 0;
let sdk;
let playerState;
const playerStates = {
  opening: "opening",
  buffering: "buffering",
  playing: "playing",
  displayingVideo: "DisplayingVideo",
  endOfStream: "endOfStream"
};
const playerCommands = {
  playerStatusChange: "playerStatusChange",
  getPlaybackTime: "getPlaybackTime",
  playerError: "playerError",
  contentAvailable: "contentAvailable",
  playerStreamingError: "playerStreamingError",
  load: "load",
  playerMessage: "playerMessage"
};
const SCE_AVPLAYER_WARNING_ID = {
  code: -2140536828,
  text: "SCE_AVPLAYER_WARNING_ID received -2140536828"
};

/**
 * When an API is called, the framework responds with a JSON formatted response parameter via the javascript command accessfunction.
 * This function must be defined by the streaming service application on the global scope.
 * That is, window.accessfunction must be defined.
 */
typeof isBrowser &&
  (window.accessfunction = function(json) {
    const data = JSON.parse(json);
    const youboraPlugin = youbora.getYouboraPlugin();
    nativeCallback(data);
    data.status === wefmafActionResponseStates.fail && manageError(data);
    data.command === playerCommands.playerMessage && manageErrorPlayerMessage(data);
    youboraPlugin && youboraPlugin.getAdapter().accessfunctionHandler(json);
  });

/**
 * Initialize the time interval to update the player's current time.
 */
function initIntervalPlayerTime() {
  stopIntervalPlayerTime();
  intervalPlayTime = setInterval(() => {
    currentTime !== -1 && emit("timeChange", currentTime);
  }, 500);
}

/**
 * Stops the time interval to update the player's current time.
 */
function stopIntervalPlayerTime() {
  clearInterval(intervalPlayTime);
  intervalPlayTime = null;
  currentTime = 0;
}

/**
 * Callback executed when an player error occurs.
 *
 * @param {Object} data Error information data.
 */
function manageError(data) {
  const commandErrorCode = commandErrors[data.command];
  if (commandErrorCode) {
    sendPlaybackErrorReport(data, commandErrorCode);
  }
}

/**
 * Callback executed when an playerMessage command is emmitted. Checks if an error occurs.
 *
 * @param {Object} data Error information data.
 */
function manageErrorPlayerMessage(data) {
  const { msg_code, msg_info } = data;
  const commandErrorCode = commandErrors[playerCommands.playerMessage];
  if (
    sdk.sendError &&
    msg_code === SCE_AVPLAYER_WARNING_ID.code &&
    msg_info === SCE_AVPLAYER_WARNING_ID.text &&
    playerState === playerStates.opening
  ) {
    sendPlaybackErrorReport(data, commandErrorCode);
    showError(wefmafActionResponseStates.fail, msg_info, commandErrorCode);
  }
}

/**
 * Sends an error report on playback issues.
 *
 * @param {Object} error - Object with information about error from accessfuncion.
 * @private
 */
function sendPlaybackErrorReport(error, commandErrorCode) {
  sdk.reportsManager.sendReport.call(
    sdk,
    { env: sdk.environment, level: "error", event: "Playback" },
    {
      eventId: "PlayerError",
      report: commandErrorCode,
      status: "error",
      parameter: {
        errorCode: commandErrorCode,
        playbackInfo: {
          playerErrorDump: error
        }
      }
    }
  );
}

/**
 * Sends an event in order to show the popup error.
 *
 * @param {String} status status of the WebMAF response.
 * @param {Object} errorInfo Error info date.
 * @param {Number} code Error code.
 */
function showError(status, errorInfo, code) {
  const sendReport = false;
  status === wefmafActionResponseStates.fail && emit("error", { code, sendReport, data: { name: errorInfo } });
}

/**
 * Initialize listeners for player commands.
 */
function initListeners() {
  sdk = this;
  addEventListener(playerCommands.playerStatusChange, data => {
    let didFirstCurrentTime = false;
    playerState = data.playerState;
    switch (playerState) {
      case playerStates.opening:
      case playerStates.buffering:
        emit("bufferStart");
        break;
      case playerStates.playing:
        emit("bufferEnd");
        break;
      case playerStates.displayingVideo:
        if (data.status === wefmafActionResponseStates.ok) {
          sdk.isDisplayingVideo = true;
          initIntervalPlayerTime();
          emit("state", states.READY);
          if (!didFirstCurrentTime) {
            emit("state", states.SUBS_READY);
            didFirstCurrentTime = true;
          }
          emit("play");
          emit("state", states.PLAYING);
        }
        break;
      case playerStates.endOfStream:
        emit("finish");
        break;
      default:
        break;
    }
  });

  addEventListener(playerCommands.getPlaybackTime, ({ elapsedTimeFloat }) => {
    currentTime = elapsedTimeFloat * 1000;
  });

  addEventListener(playerCommands.playerError, ({ status, error_info }) => {
    showError(status, error_info, commandErrors[playerCommands.playerError]);
  });

  addEventListener(playerCommands.contentAvailable, ({ status, error_info }) => {
    showError(status, error_info, commandErrors[playerCommands.contentAvailable]);
  });

  addEventListener(playerCommands.playerStreamingError, ({ status, error_info }) => {
    showError(status, error_info, commandErrors[playerCommands.playerStreamingError]);
  });

  addEventListener(playerCommands.load, ({ status, error_info }) => {
    showError(status, error_info, commandErrors[playerCommands.load]);
  });
}

export { initIntervalPlayerTime, stopIntervalPlayerTime, initListeners };
